import {
    SystemAdminApi,
    AppFeedbackAdminDetailDto,
    AppFeedbackAdminListDto,
    AppFeedbackCategory,
    AppFeedbackReviewCandidateDto,
    AppFeedbackReviewSettingsDto,
    AppFeedbackReviewSettingsUpdateResultDto,
    UpdateAppFeedbackReviewSettingsDto,
    CreateTenantDto,
    UpdateTenantDto,
    User,
    CheckUserExistsDto,
    UserExistsResponse,
    AddExistingUserDto,
    CreateNewUserDto
} from "breuer-supercharged-sdk";
import { getClient } from "./client";

export class SystemAdminService {
    private static instance: SystemAdminService;

    private constructor() { }

    public static getInstance(): SystemAdminService {
        if (!SystemAdminService.instance) {
            SystemAdminService.instance = new SystemAdminService();
        }
        return SystemAdminService.instance;
    }

    private client = new SystemAdminApi(undefined, undefined, getClient())

    // Tenant Management
    async getAllTenants() {
        const response = await this.client.systemAdminControllerGetAllTenants();
        return (response).data;
    }

    async getTenantById(tenantId: string) {
        const response = await this.client.systemAdminControllerGetTenantById(tenantId);
        return (response).data;
    }

    async createTenant(createTenantDto: CreateTenantDto) {
        const response = await this.client.systemAdminControllerCreateTenant(createTenantDto);
        return (response).data;
    }

    async updateTenant(tenantId: string, updateTenantDto: UpdateTenantDto) {
        const response = await this.client.systemAdminControllerUpdateTenant(tenantId, updateTenantDto);
        return (response).data;
    }

    async deleteTenant(tenantId: string): Promise<void> {
        await this.client.systemAdminControllerDeleteTenant(tenantId);
    }

    async blockTenant(tenantId: string, isActive: boolean) {
        // Use updateTenant with isActive field since the blockTenant endpoint expects a body
        const response = await this.client.systemAdminControllerUpdateTenant(tenantId, { isActive });
        return (response).data;
    }

    async getTenantUsers(tenantId: string): Promise<User[]> {
        const response = await this.client.systemAdminControllerGetTenantUsers(tenantId);
        return (response).data;
    }

    async checkTenantUserExists(tenantId: string, checkUserExistsDto: CheckUserExistsDto): Promise<UserExistsResponse> {
        const response = await this.client.systemAdminControllerCheckTenantUserExists(tenantId, checkUserExistsDto);
        return (response).data;
    }

    async addExistingUserToTenant(tenantId: string, addExistingUserDto: AddExistingUserDto): Promise<User> {
        const response = await this.client.systemAdminControllerAddExistingUserToTenant(tenantId, addExistingUserDto);
        return (response).data;
    }

    async createNewUserInTenant(tenantId: string, createNewUserDto: CreateNewUserDto): Promise<User> {
        const response = await this.client.systemAdminControllerCreateNewUserInTenant(tenantId, createNewUserDto);
        return (response).data;
    }

    // Feedback-Prüfung je Mandant
    async getFeedbackReviewSettings(tenantId: string): Promise<AppFeedbackReviewSettingsDto> {
        return (await this.client.systemAdminGetAppFeedbackReviewSettings(tenantId)).data;
    }

    async updateFeedbackReviewSettings(tenantId: string, dto: UpdateAppFeedbackReviewSettingsDto): Promise<AppFeedbackReviewSettingsUpdateResultDto> {
        return (await this.client.systemAdminUpdateAppFeedbackReviewSettings(tenantId, dto)).data;
    }

    async listFeedbackReviewCandidates(tenantId: string): Promise<AppFeedbackReviewCandidateDto[]> {
        return (await this.client.systemAdminListAppFeedbackReviewCandidates(tenantId)).data;
    }

    // Billing Statistics
    async getAllTenantsBillingStatistics(startDate?: string, endDate?: string) {
        const response = await this.client.systemAdminControllerGetAllTenantsBillingStatistics(startDate, endDate);
        return (response).data;
    }

    async getTenantBillingStatistics(tenantId: string, startDate?: string, endDate?: string) {
        const response = await this.client.systemAdminControllerGetTenantBillingStatistics(tenantId, startDate, endDate);
        return (response).data;
    }

    async getTenantBillingHistory(tenantId: string, startDate?: string, endDate?: string, actionType?: string) {
        const response = await this.client.systemAdminControllerGetTenantBillingHistory(tenantId, startDate, endDate, actionType);
        return (response).data;
    }

    // In-app feedback of all tenants (newest first)
    async listAppFeedback(params: {
        limit?: number;
        offset?: number;
        tenantId?: string;
        category?: AppFeedbackCategory;
    }): Promise<AppFeedbackAdminListDto> {
        const response = await this.client.systemAdminListAppFeedback(
            params.limit,
            params.offset,
            params.tenantId,
            params.category,
        );
        return response.data;
    }

    // One feedback entry with logs + signed screenshot/attachment URLs (1 h)
    async getAppFeedback(feedbackId: string): Promise<AppFeedbackAdminDetailDto> {
        const response = await this.client.systemAdminGetAppFeedback(feedbackId);
        return response.data;
    }
}